import React, { Suspense, useEffect, useMemo, useState } from "react";
import PageLoader from "./components/shared/PageLoader";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, restoreAuth } from "./redux/slices/authSlice";
import { getTheme } from "./helper/theme/theme";
import { useAppRoutes, useAppRoutesLoaded, loadAppRoutes, loginRoute, dashboardRoute, subscriptionRoute } from "./utils/appRoutes";
import { pageComponentFor } from "./utils/pageRegistry";
import GenericModulePage from "./pages/GenericModulePage";
import { refreshMyModules } from "./helper/refreshModules";
import { isGroupOnlyModule } from "./helper/libs/is-group-only-module";
import CustomerProtectedRoute from "./components/auth/CustomerProtectedRoute";
import AdminProtectedRoute from "./components/auth/AdminProtectedRoute";
import Layout from "./pages/dashboard/layout";
import AdminLayout from "./pages/dashboard/AdminLayout";
import { BulkJobProvider } from "./context/BulkJobContext";
import { SellviaCleanupJobProvider } from "./context/SellviaCleanupJobContext";
import { SellviaActivityProvider } from "./context/SellviaActivityContext";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import api from "./utils/api";
import { notifyError } from "./helper/notify";

// Loading component
const LoadingComponent = () => <PageLoader fullScreen />;

// Each page gets its OWN Suspense boundary, keyed by its route. Navigating to a not-yet-loaded page then shows the
// loading animation straight away (inside the layout, sidebar stays) instead of React keeping the OLD page on screen
// until the new one has loaded — which looked like "the URL changed but the old page's data is still showing".
const PageSuspense = ({ id, children }) => (
  <Suspense key={id} fallback={<PageLoader />}>{children}</Suspense>
);

// Helper function to get first accessible module
const getFirstAccessibleModule = (modules, role, side) => {
  // A customer with no plan has no module with any permission: the plan page is the only place for them
  if (side === "customer" && !(modules || []).some((m) => (m.permissions || []).length > 0)) return subscriptionRoute();
  if (!modules || modules.length === 0) {
    if (side === "customer") {
      return dashboardRoute(false, modules);
    } else if (["admin", "super-admin", "superadmin"].includes(role?.toLowerCase())) {
      return dashboardRoute(true, modules);
    } else if (role?.toLowerCase() === "customer") {
      return dashboardRoute(false, modules);
    }
    return loginRoute(false);
  }

  // Sort modules by order
  const sortedModules = [...modules].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : 999;
    const orderB = b.order !== undefined ? b.order : 999;
    return orderA - orderB;
  });

  // Skip group-only modules (sidebar headings with no page of their own).
  const firstModule = sortedModules.find(m => m.route_path && !isGroupOnlyModule(m, modules));
  
  if (firstModule) {
    return firstModule.route_path;
  }

  // Fallback
  if (side === "customer") {
    return dashboardRoute(false, modules);
  } else if (["admin", "super-admin", "superadmin"].includes(role?.toLowerCase())) {
    return dashboardRoute(true, modules);
  } else if (role?.toLowerCase() === "customer") {
    return dashboardRoute(false, modules);
  }
  return loginRoute(false);
};

// Create a wrapper component for the interceptor logic
const AppContent = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userRole = useSelector((state) => state.auth.role);
  const side = useSelector((state) => state.auth.side);
  const modules = useSelector((state) => state.auth.modules);

  // Routes that are not sidebar modules (login, landing, Settings sub-paths …) come from the database too.
  const appRoutes = useAppRoutes();
  const routesLoaded = useAppRoutesLoaded();
  const [routesFailed, setRoutesFailed] = useState(false);
  const fetchRoutes = () => { setRoutesFailed(false); loadAppRoutes().then((ok) => setRoutesFailed(!ok)); };
  useEffect(() => { fetchRoutes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The module list saved in the browser may be from before a module/page change (or lack page_key) — on every
  // app start while logged in, read the current one from the server so routes + sidebar are always up to date.
  useEffect(() => { if (isAuthenticated) refreshMyModules(); }, [isAuthenticated]);

  // Routes of the logged-in user's modules (database): path = module.route_path, page = module.page_key from the
  // registry, else the Default page. A module that only groups other modules (sidebar folder) has no page.
  // Static routes in config.js win, so a path is never registered twice.
  const moduleRoutes = useMemo(() => {
    const staticPaths = new Set(appRoutes.map((r) => r.path));
    const list = Array.isArray(modules) ? modules : [];
    const parentIds = new Set(list.map((m) => String(m.parent_id || '')).filter(Boolean));
    const seen = new Set();
    const out = [];
    list.forEach((m) => {
      const path = String(m.route_path || '').trim();
      if (!path || !path.startsWith('/') || m.is_active === false || m.is_group || staticPaths.has(path) || seen.has(path)) return;
      const Registered = pageComponentFor(m.page_key);
      if (!Registered && parentIds.has(String(m.moduleId || m._id))) return; // folder only
      seen.add(path);
      out.push({ path, Component: Registered || GenericModulePage });
    });
    return out;
  }, [modules, appRoutes]);
  const token = useSelector((state) => state.auth.token);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(restoreAuth());
    
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    setIsLoading(false);
    
    const handleBeforeUnload = () => {
      const path = window.location.pathname;
      if (path !== loginRoute(false) && path !== loginRoute(true)) {
        sessionStorage.setItem('lastVisitedRoute', path);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch, token]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Save current page before logout so login can redirect back here —
          // same convention used by the other logout paths in the app.
          const currentPath = window.location.pathname + window.location.search;
          const isAdminArea = currentPath.startsWith('/admin');
          sessionStorage.setItem(isAdminArea ? 'lastAdminPage' : 'lastUserPage', currentPath);

          dispatch(logout());
          sessionStorage.removeItem('authState');
          notifyError('Your session has expired. Please login again.', {
            toastId: 'token-expired'
          });

          // The admin login route is "/admin" itself — there is no "/admin/login" route.
          window.location.href = `${loginRoute(isAdminArea)}?expired=true`;
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [dispatch]);

  if (!routesLoaded && routesFailed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, fontFamily: 'sans-serif', color: '#1d1a14', background: '#f6f1e6' }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Cannot reach the server</div>
        <div style={{ fontSize: 13, color: '#6b6459' }}>The app could not load its pages. Check your connection and try again.</div>
        <button onClick={fetchRoutes} style={{ padding: '8px 20px', borderRadius: 10, border: 'none', background: '#6f5212', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (isLoading || !routesLoaded) {
    return <LoadingComponent />;
  }

  return (
    <>
      {/* z-index set well above any modal in the app (e.g. the Sellvia
          store-settings popup uses 10000) so toasts always render on top,
          fully sharp — never behind a modal's blurred backdrop. */}
      <ToastContainer position="top-center" autoClose={5000} style={{ zIndex: 999999 }} />
      {/* Every page is lazy-loaded. Without a Suspense boundary, a page that loads while React is handling a normal
          (synchronous) update — a hot reload, a logout, the modules list refreshing — crashes with "A component
          suspended while responding to synchronous input". The boundary shows the loading screen instead. */}
      <Suspense fallback={<LoadingComponent />}>
      <Routes>
        {/* App routes (database: AppRoute) — public pages, Settings sub-paths, standalone pages */}
        {appRoutes.map(({ name, path, page_key: pageKey, layout, redirect_if_authenticated: redirectIfAuth }) => {
          const Component = pageComponentFor(pageKey);
          if (!Component) return null;
          return (
            <Route
              key={`app:${name}`}
              path={path}
              element={
                layout === 'admin' ? (
                  <AdminProtectedRoute>
                    <AdminLayout>
                      <PageSuspense id={path}><Component /></PageSuspense>
                    </AdminLayout>
                  </AdminProtectedRoute>
                ) : layout === 'customer' ? (
                  <CustomerProtectedRoute>
                    <Layout>
                      <PageSuspense id={path}><Component /></PageSuspense>
                    </Layout>
                  </CustomerProtectedRoute>
                ) : isAuthenticated && redirectIfAuth ? (
                  // A logged-in user is sent on to their FIRST module instead of seeing a login page
                  <Navigate to={getFirstAccessibleModule(modules, userRole, side)} replace />
                ) : (
                  <Component />
                )
              }
            />
          );
        })}

        {/* Module routes — built from the user's modules (database), not from config.js */}
        {moduleRoutes.map(({ path, Component }) => (
          <Route
            key={`module:${path}`}
            path={path}
            element={
              path.startsWith('/admin') ? (
                <AdminProtectedRoute>
                  <AdminLayout>
                    <PageSuspense id={path}><Component /></PageSuspense>
                  </AdminLayout>
                </AdminProtectedRoute>
              ) : (
                <CustomerProtectedRoute>
                  <Layout>
                    <PageSuspense id={path}><Component /></PageSuspense>
                  </Layout>
                </CustomerProtectedRoute>
              )
            }
          />
        ))}

        {/* Catch-all route - redirect to FIRST module */}
        <Route
          path="*"
          element={
            <Navigate
              to={isAuthenticated ? 
                getFirstAccessibleModule(modules, userRole, side) 
                // Logged out: an admin URL goes back to the ADMIN login, anything else to the customer login.
                // (Module routes disappear the moment the modules are cleared on logout, so this is where a
                // logout from an admin page lands.)
                : loginRoute(window.location.pathname.startsWith('/admin'))}
              replace
            />
          }
        />
      </Routes>
      </Suspense>
    </>
  );
};

const App = () => {
  const mode = useSelector((state) => state.ui.mode);
  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "000000000000-placeholder.apps.googleusercontent.com"}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <BulkJobProvider>
            <SellviaCleanupJobProvider>
              <SellviaActivityProvider>
                <AppContent />
              </SellviaActivityProvider>
            </SellviaCleanupJobProvider>
          </BulkJobProvider>
        </Router>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;